/** 프로젝트 생성(위저드 → 모델 조립)·2단계 삭제·이동(ProjectPickerPage 에서 분리, P4-c) */
import { useCallback, useMemo } from 'react';
import { useProjectStore } from '@/app/stores/projectStore';
import { useWorkspaceStore } from '@/app/stores/workspaceStore';
import { isWorkspaceId } from '@/app/workspaceFilter';
import type { NewProjectFormData } from '@/features/project-picker';
import type { ProjectSummary, RestartBaseline, SerializedEdge, SerializedNode, SerializedSettings } from '@/types/project';
import { roleFields } from '@/types/scope';
import { useAsyncScope } from '@/shared/async/useAsyncScope';
import { useKeyedState } from '@/shared/async/useKeyedState';
import { QUICKRUN_PROJECT_ID } from '@/app/quickRunProject';
import { DEFAULT_MARS_CONFIG } from '@/features/project-picker/NewProjectWizard/marsConfigDefault';
import type { ProjectFromFilePlan } from '@/features/project-picker/projectFromJson';
import { storageOwner } from '@/app/services/storage/signedInStorageOwner';
import { storedName } from '@/domain/namePolicy';

export type Notice = { message: string; severity: 'success' | 'error' };

/** wsSelection: 'all' | 워크스페이스 id | null(보류). 생성은 선택이 워크스페이스 id 일 때 그 id 에, 'all' 이면 서버가 기본 워크스페이스에 만든다 */
export function useProjectMutations(wsSelection: string | null, notify: (n: Notice) => void) {
  const { projects, createProject, deleteProject } = useProjectStore();
  const { workspaces, moveProject } = useWorkspaceStore();
  const scopeKey = wsSelection ?? '';
  // 오버레이·범위는 워크스페이스 선택에 속한다 — ?ws 가 바뀌면 옛 위저드·삭제·이동 오버레이가 살아남지 않고,
  // 진행 중 요청의 완료가 새 워크스페이스 화면을 갱신하지 않는다(R15)
  const [newProjectDialogOpen, setNewProjectDialogOpen] = useKeyedState(scopeKey, false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useKeyedState(scopeKey, false);
  const [forceConfirm, setForceConfirm] = useKeyedState<{ id: string; remainingFiles: number } | null>(scopeKey, null);
  const [projectToDelete, setProjectToDelete] = useKeyedState<string | null>(scopeKey, null);
  const [moveTarget, setMoveTarget] = useKeyedState<ProjectSummary | null>(scopeKey, null);
  const scope = useAsyncScope(scopeKey);
  // 보고 있는 워크스페이스에 만든다('all' 은 서버가 기본 워크스페이스에)
  const creationWorkspace = useMemo(() => (wsSelection !== null && isWorkspaceId(wsSelection) ? { workspace_id: wsSelection } : {}), [wsSelection]);

  /** 위저드 파티션 → 모델(RESTART 는 소스 모델의 그래프·설정을 복제) → 프로젝트 생성 */
  const handleCreateProject = useCallback(async (formData: NewProjectFormData): Promise<string | null> => {
    const owns = scope.claim();
    try {
      const now = new Date().toISOString();
      const models = await Promise.all(formData.partitions.map(async (partition) => {
        let nodes: SerializedNode[] = [];
        let edges: SerializedEdge[] = [];
        let settings: SerializedSettings & { card100?: { problemType?: string; calculationType?: string } } = {};
        let restartBaseline: RestartBaseline | undefined;
        if (partition.analysisCode === 'MARS' && partition.marsConfig?.problemType === 'RESTART' && partition.marsConfig?.restartSource?.projectId && partition.marsConfig?.restartSource?.modelId) {
          const source = partition.marsConfig.restartSource;
          if (!source.simulationId) {
            throw new Error(`${partition.name}: 재시작할 원본 실행을 선택해주세요`);
          }
          // 목록 항목은 본문 없는 요약 — 산출물 소유자를 알려면 원본 본문이 필요하다
          const sourceProject = (await useProjectStore.getState().fetchProjectById(source.projectId)) ?? undefined;
          if (!sourceProject) {
            throw new Error(`${partition.name}: 원본 프로젝트를 찾지 못해 기준선을 세울 수 없습니다`);
          }
          // 원본 실행의 자리 소유자 — 판정과 사유는 storageOwner 하나(로그인 사용자, 그리고 원본 프로젝트의 주인이어야 한다)
          const owner = storageOwner('기준선을 세울', sourceProject);
          if (!owner.ok) throw new Error(`${partition.name}: ${owner.reason}`);
          // **기준선은 원본 실행의 짝 덱에서 한 번만 판다**(설계 §4.1).
          // 원본 모델의 현재 그래프를 복제하면 rstplt 와 짝이 맞지 않고,
          // 기준선을 따로 파생하면 신원이 갈려 편집 없이도 전부 삭제로 잡힌다.
          const { restartModelSeed } = await import('@/app/authoring/restartModelSeed');
          const { storageService } = await import('@/app/services/storage/storageService');
          const seed = await restartModelSeed({
            source: {
              userId: owner.userId,
              projectId: source.projectId,
              modelId: source.modelId,
              simulationId: source.simulationId,
              taskIndex: source.taskIndex ?? 0,
            },
            modelName: `${storedName(formData.title)} - ${storedName(partition.name)}`,
            downloadDeck: async (objectKey) => {
              const file = await storageService.downloadFile(objectKey);
              return new TextDecoder().decode(file.content);
            },
            downloadOutput: async (objectKey) => {
              const file = await storageService.downloadFile(objectKey);
              return new TextDecoder().decode(file.content);
            },
            restartNumber: source.restartNumber ?? -1,
          });
          nodes = seed.nodes as unknown as SerializedNode[];
          edges = seed.edges as unknown as SerializedEdge[];
          settings = (seed.settings ?? {}) as typeof settings;
          restartBaseline = seed.restartBaseline;
        }
        if (partition.analysisCode === 'MARS') {
          // 마법사가 보여 준 값이 곧 저장값(#420): 손대지 않은 기본값도 싣는다. 설정은 에디터가 새 모델에 쓰는 것과 같은
          // 정본 기본값(getDefaultGlobalSettings — projectSlice 의 초기 상태와 동일) 위에 얹는다. 빈 객체 `{}` 는 생성기가
          // 글로벌 카드를 전부 건너뛰어 첫 검증이 "cards 100 missing" 으로 막혔다. RESTART 는 소스 모델 설정을 복제했으므로 그 위에 얹는다.
          const marsConfig = { ...(partition.marsConfig ?? DEFAULT_MARS_CONFIG) }; // 사본 — 모델마다 자기 객체
          // 정본 기본값은 검증 도메인(MARS 도메인 전체를 끌어옴)에 있어 쓰는 시점에 받는다 — 진입 청크 밖(트랙 A-4)
          const { getDefaultGlobalSettings } = await import('@/domain/validation/globalSettingsValidation');
          const base = Object.keys(settings).length > 0 ? settings : (getDefaultGlobalSettings() as unknown as typeof settings);
          settings = { ...base, marsConfig };
          settings.card100 = {
            ...settings.card100,
            problemType: marsConfig.problemType.toLowerCase() as 'new' | 'restart',
            // 마법사의 선택(problemOption 은 필수 필드·항상 표시)이 곧 저장값 — RESTART 도 화면에 보인 값이 실린다
            calculationType: marsConfig.problemOption.toLowerCase() as 'transnt' | 'stdy-st',
          };
          if (marsConfig.problemType === 'RESTART' && marsConfig.restartSource) {
            settings.card103 = { restartNumber: marsConfig.restartSource.restartNumber ?? -1, rstpltFileName: 'rstplt' };
          }
        }
        return {
          id: partition.id, name: storedName(partition.name), analysisCodes: [partition.analysisCode], description: partition.description || null,
          scope: { systems: partition.scope, components: [] }, ...roleFields(partition.role), nodes, edges, settings,
          ...(restartBaseline ? { restartBaseline } : {}),
          updateHistory: [], created_at: now, updated_at: now,
        };
      }));
      const newProject = await createProject({
        name: storedName(formData.title),
        description: formData.description || null,
        ...creationWorkspace,
        data: { totalScope: { systems: formData.scope, components: [] }, models, updateHistory: [{ version: '1.0', timestamp: now, author: 'System', description: '프로젝트 생성' }] },
      });
      if (!owns()) return null;
      if (newProject) { notify({ message: `프로젝트 "${storedName(formData.title)}"가 생성되었습니다.`, severity: 'success' }); return newProject.id; }
      notify({ message: useProjectStore.getState().error || '프로젝트 생성에 실패했습니다.', severity: 'error' });
      return null;
    } catch (err) {
      console.error('[ProjectPickerPage] handleCreateProject error:', err);
      if (owns()) notify({ message: `프로젝트 생성에 실패했습니다: ${err instanceof Error ? err.message : String(err)}`, severity: 'error' });
      return null;
    }
  }, [createProject, creationWorkspace, notify, scope]);

  /** JSON 파일 계획 → 프로젝트 생성 — 계획이 data 를 다 정했으므로 여기는 워크스페이스·알림만 */
  const handleCreateProjectFromFile = useCallback(async (plan: ProjectFromFilePlan): Promise<string | null> => {
    const owns = scope.claim();
    const newProject = await createProject({
      name: plan.name,
      description: null,
      ...creationWorkspace,
      data: plan.data,
    });
    if (!owns()) return null;
    if (newProject) { notify({ message: `프로젝트 "${plan.name}"가 생성되었습니다.`, severity: 'success' }); return newProject.id; }
    notify({ message: useProjectStore.getState().error || '프로젝트 생성에 실패했습니다.', severity: 'error' });
    return null;
  }, [createProject, creationWorkspace, notify, scope]);

  const handleDeleteClick = useCallback((projectId: string) => {
    if (projectId === QUICKRUN_PROJECT_ID) { notify({ message: '기본 프로젝트는 삭제할 수 없습니다.', severity: 'error' }); return; }
    setProjectToDelete(projectId);
    setDeleteDialogOpen(true);
  }, [notify, setProjectToDelete, setDeleteDialogOpen]);
  /** 삭제 결과를 스낵바로 알린다 — 성공/실패 문구가 한 곳에만 있도록 */
  const notifyDeleteOutcome = useCallback((deleted: boolean) => {
    notify({ message: deleted ? '프로젝트가 삭제되었습니다.' : '프로젝트 삭제에 실패했습니다.', severity: deleted ? 'success' : 'error' });
  }, [notify]);
  const handleDeleteConfirm = useCallback(async () => {
    if (!projectToDelete) return;
    const owns = scope.claim();
    const outcome = await deleteProject(projectToDelete);
    if (!owns()) return;
    setDeleteDialogOpen(false);
    if (outcome.status === 'needs-force') { setForceConfirm({ id: projectToDelete, remainingFiles: outcome.remainingFiles }); return; }
    notifyDeleteOutcome(outcome.status === 'deleted');
    setProjectToDelete(null);
  }, [projectToDelete, deleteProject, notifyDeleteOutcome, scope, setDeleteDialogOpen, setForceConfirm, setProjectToDelete]);
  /** 2단계: 사용자가 파일까지 함께 지우겠다고 확인한 뒤 */
  const handleForceDeleteConfirm = useCallback(async () => {
    if (!forceConfirm) return;
    const owns = scope.claim();
    const outcome = await deleteProject(forceConfirm.id, true);
    if (!owns()) return;
    setForceConfirm(null);
    setProjectToDelete(null);
    notifyDeleteOutcome(outcome.status === 'deleted');
  }, [forceConfirm, deleteProject, notifyDeleteOutcome, scope, setForceConfirm, setProjectToDelete]);
  const handleForceDeleteCancel = useCallback(() => { setForceConfirm(null); setProjectToDelete(null); }, [setForceConfirm, setProjectToDelete]);

  const handleMoveClick = useCallback((projectId: string) => {
    if (projectId === QUICKRUN_PROJECT_ID) { notify({ message: '기본 프로젝트는 이동할 수 없습니다.', severity: 'error' }); return; }
    const project = projects.find((p) => p.id === projectId);
    if (project) setMoveTarget(project);
  }, [projects, notify, setMoveTarget]);
  const handleMoveConfirm = useCallback(async (projectId: string, workspaceId: string) => {
    const owns = scope.claim();
    const moved = await moveProject(projectId, workspaceId);
    if (!owns()) return;
    setMoveTarget(null);
    if (moved.ok) {
      const name = workspaces.find((w) => w.id === workspaceId)?.name;
      notify({ message: name ? `프로젝트가 "${name}"(으)로 이동되었습니다.` : '프로젝트가 이동되었습니다.', severity: 'success' });
    }
    else notify({ message: moved.error, severity: 'error' }); // 그 호출의 사유 — 스토어 error 칸은 읽지 않는다
  }, [moveProject, workspaces, notify, scope, setMoveTarget]);

  return {
    newProjectDialogOpen, setNewProjectDialogOpen, deleteDialogOpen, setDeleteDialogOpen, forceConfirm, moveTarget, setMoveTarget,
    handleCreateProject, handleCreateProjectFromFile, handleDeleteClick, handleDeleteConfirm, handleForceDeleteConfirm, handleForceDeleteCancel, handleMoveClick, handleMoveConfirm,
  };
}
